import type { BookingAnalyticsData } from "@/screens/BookingDetail/NewPatronBookingFlow/types";

import {
  buildBookingConfirmationAnalyticsData,
  type ConfirmedBooking,
} from "../bookings/buildBookingConfirmationAnalyticsData";
import {
  createNormalizedConversionEvent,
  trackConversionEvent,
} from "./conversionAnalytics";
import type {
  ConversionAnalyticsConfig,
  ConversionAnalyticsGlobals,
  ConversionEventName,
} from "./types";

type BookingConfirmationEventName = Extract<
  ConversionEventName,
  | "booking_confirmed"
  | "multi_booking_confirmed"
  | "dining_reservation_confirmed"
>;

type TrackBookingConfirmationConversionInput = {
  success: boolean | null | undefined;
  eventName: BookingConfirmationEventName;
  analyticsData: BookingAnalyticsData;
  bookings: ReadonlyArray<ConfirmedBooking>;
  config?: ConversionAnalyticsConfig;
  globals?: ConversionAnalyticsGlobals;
};

export const trackBookingConfirmationConversion = ({
  success,
  eventName,
  analyticsData,
  bookings,
  config,
  globals,
}: TrackBookingConfirmationConversionInput): void => {
  if (success !== true || !analyticsData || bookings.length === 0) return;

  const confirmationAnalyticsData = buildBookingConfirmationAnalyticsData(
    analyticsData,
    bookings
  );

  if (!confirmationAnalyticsData) return;

  const hasBookingIdentifier =
    !!confirmationAnalyticsData.bookingId ||
    (confirmationAnalyticsData.bookingIds?.length ?? 0) > 0;

  if (!hasBookingIdentifier) return;

  trackConversionEvent(
    createNormalizedConversionEvent({
      eventName,
      bookingAnalyticsData: confirmationAnalyticsData,
      bookingId: confirmationAnalyticsData.bookingId,
      bookingIds: confirmationAnalyticsData.bookingIds,
      orderId: confirmationAnalyticsData.orderId,
      transactionId: confirmationAnalyticsData.transactionId,
      value: confirmationAnalyticsData.value,
      currency: confirmationAnalyticsData.currency,
    }),
    config,
    globals
  );
};
